import { FC, useEffect } from 'react'
import { Form, Input, InputNumber, Modal, Select } from 'antd'
import { TDnsRecord, TDnsRecordType } from '../types'

const RECORD_TYPES: TDnsRecordType[] = ['A', 'AAAA', 'CNAME', 'TXT', 'MX']

type TRecordForm = {
 type: string
 host: string
 value: string
 priority?: number | null
}

type Props = {
 visible: boolean
 // Set when editing; undefined opens an empty "Add a record" form
 record?: TDnsRecord | null
 saving?: boolean
 onCancel: () => void
 // Parent closes the modal itself (after its API call succeeds, if any)
 onSave: (record: TDnsRecord) => void
}

// Add/edit record modal shared by the wizard review step and DNS Records
const RecordModal: FC<Props> = ({
 visible,
 record,
 saving,
 onCancel,
 onSave,
}) => {
 const [form] = Form.useForm<TRecordForm>()
 const type = Form.useWatch('type', form)

 // Reset the form each time the modal opens
 useEffect(() => {
  if (!visible) return
  form.setFieldsValue({
   type: record?.type ?? 'A',
   host: record?.host ?? '',
   value: record?.value ?? '',
   priority: record?.priority ?? null,
  })
 }, [visible, record, form])

 const handleOk = async (): Promise<void> => {
  const values = await form.validateFields()
  onSave({
   ...record,
   type: values.type,
   // Empty host means the root domain
   host: values.host?.trim() || '@',
   value: values.value.trim(),
   priority: values.type === 'MX' ? values.priority ?? null : null,
  })
 }

 return (
  <Modal
   visible={visible}
   title={record ? 'Edit record' : 'Add a record'}
   okText="Save record"
   onOk={handleOk}
   onCancel={onCancel}
   confirmLoading={saving}
   width={400}
   className="domain-record-modal"
   destroyOnClose
  >
   <p className="modal-sub">
    Only add this if you know what it&apos;s for — incorrect records can break
    email or other services.
   </p>
   <Form form={form} layout="vertical" requiredMark={false}>
    <Form.Item label="Type" name="type">
     <Select
      options={RECORD_TYPES.map((t) => ({ label: t, value: t }))}
      getPopupContainer={(trigger) => trigger.parentElement}
     />
    </Form.Item>
    <Form.Item label="Host" name="host">
     <Input placeholder="@ or www" />
    </Form.Item>
    <Form.Item
     label="Value"
     name="value"
     rules={[{ required: true, whitespace: true, message: 'Enter a value' }]}
    >
     <Input placeholder="e.g. 76.223.10.44" />
    </Form.Item>
    {type === 'MX' && (
     <Form.Item
      label="Priority"
      name="priority"
      rules={[{ required: true, message: 'Enter a priority' }]}
     >
      <InputNumber min={0} placeholder="10" style={{ width: '100%' }} />
     </Form.Item>
    )}
   </Form>
  </Modal>
 )
}

export default RecordModal
